import { EntityTypeEnum } from 'api/activity-feed';
import { ActivityRowLike } from 'features/activity-feed/utils/activityNavigation';

/**
 * Category of the activity feed entries posted by the back for a quote validation
 * request (request, answer, cancellation, invalidation). Linked entities: the
 * `DemandeValidation` (request id) and the `Devis`.
 */
export const VALIDATION_NOTIFICATION_CATEGORY = 'validation';

/** A validation request entry? (case-insensitive comparison) */
export const isValidationNotification = (item: ActivityRowLike): boolean =>
  (item.category ?? '').toLowerCase() === VALIDATION_NOTIFICATION_CATEGORY;

interface ValidationRowUrlRights {
  /** Validator session (Capability.ValidationsAccess). */
  canOpenValidations: boolean;
  /** Broker session (Capability.ActivityFeedLinks). */
  canFollowLinks: boolean;
}

/**
 * Destination of a validation entry, or null when it is not one (or when the
 * session may open neither target):
 *   - validator session -> the request on the validator screen (`/validations/{id}`);
 *   - broker session -> the contractualisation of the quote, where the answer is
 *     applied (`/simulation/contractualisation/{riskId}/{quoteId}`).
 * The validator case does not depend on ActivityFeedLinks (Distribution only).
 */
export const getValidationRowUrl = (
  item: ActivityRowLike,
  { canOpenValidations, canFollowLinks }: ValidationRowUrlRights
): string | null => {
  if (!isValidationNotification(item)) return null;

  const request = item.entities?.find(
    (e) => e.entityType === EntityTypeEnum.DemandeValidation
  );
  if (canOpenValidations && request) return `/validations/${request.entityId}`;

  const quote = item.entities?.find((e) => e.entityType === EntityTypeEnum.Devis);
  const riskId = item.riskId ?? item.context?.riskId;
  if (canFollowLinks && quote && riskId) {
    const borrowerProjectId = item.context?.borrowerProjectId;
    return borrowerProjectId
      ? `/simulation/contractualisation/borrower/${borrowerProjectId}/${riskId}/${quote.entityId}`
      : `/simulation/contractualisation/${riskId}/${quote.entityId}`;
  }

  return null;
};
